import React from 'react';
import {
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  useTheme,
} from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import { Items } from '@prisma/client';
import { useFormikContext } from 'formik';
import useSearchItems from '../../hooks/items/useSearchItems';
import CheckoutItemsSearchRow, {
  HEIGHT,
  SPACING,
} from './CheckoutItemsSearchRow';
import { CheckoutFormValues } from '../../types/appointments';
import useItemQuery from '../../hooks/checkouts/useItemQuery';
import useUser from '../../hooks/global/useUser';
import { Appointment } from '../../interfaces';

interface Props {
  appointment?: Appointment | null;
}

function CheckoutItemsSearch({ appointment }: Props) {
  const theme = useTheme();
  const { user } = useUser();
  const [query, setQuery] = useItemQuery();
  const [isFocused, setIsFocused] = React.useState(false);
  const [focusedIndex, setFocusedIndex] = React.useState(0);

  const allowItemLibrary =
    !appointment ||
    (!!user?.business?.id &&
      !!appointment?.businessId &&
      user?.business?.id === appointment?.businessId);

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const { values, setFieldValue } = useFormikContext<CheckoutFormValues>();

  const {
    items: searchItems,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isDebouncing,
  } = useSearchItems(query, {
    enabled: isFocused && allowItemLibrary,
  });
  const items = allowItemLibrary ? searchItems : [];

  const onAddItem = (item: Items | null) => {
    const id = item?.id || Date.now().toString();

    const existingItem = item
      ? values.customOrderItems?.some(i => i.itemId === item?.id)
      : undefined;

    if (existingItem) {
      const updatedItems = values.customOrderItems?.map(i =>
        i.itemId === item?.id ? { ...i, quantity: i.quantity + 1 } : i,
      );
      setFieldValue('customOrderItems', updatedItems ?? []);
    } else {
      const newItem: NonNullable<
        CheckoutFormValues['customOrderItems']
      >[number] = !item
        ? !Number(query)
          ? { id, name: query || 'Custom Item', quantity: 1, price: 0 }
          : {
              id,
              name: 'Custom Amount',
              quantity: 1,
              price: parseFloat(query),
            }
        : {
            id,
            name: item.name,
            price: item.price,
            quantity: 1,
            itemId: item.id,
          };

      setFieldValue('customOrderItems', [
        ...(values.customOrderItems ?? []),
        newItem,
      ]);
    }
    inputRef.current?.blur();
    setIsFocused(false);
    setQuery('');
  };

  const onFocus = () => {
    setIsFocused(true);
    setFocusedIndex(0);
  };
  const onBlur = () => {
    setIsFocused(false);
    setFocusedIndex(0);
  };
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!isFocused) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      onAddItem(items[focusedIndex - 1] || null);
    }
    if (event.key === 'ArrowDown') {
      setFocusedIndex(prev => Math.min(prev + 1, items.length));
    }
    if (event.key === 'ArrowUp') {
      setFocusedIndex(prev => Math.max(prev - 1, 0));
    }
  };

  const itemContent = (index: number, item: Items | null) => (
    <CheckoutItemsSearchRow
      item={item}
      index={index}
      totalCount={items.length + 1}
      isFocused={focusedIndex === index}
      onAddItem={onAddItem}
    />
  );

  return (
    <Stack width="100%" position="relative">
      <TextField
        aria-describedby="items-list"
        inputRef={inputRef}
        id="addItem"
        type="text"
        className="table-input"
        value={query}
        placeholder="Select an item or add an amount"
        fullWidth
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onChange={(event: any) => {
          setQuery(event.target.value);
        }}
        slotProps={{
          input: {
            endAdornment:
              isFetching || isDebouncing ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} color="primary" />
                </InputAdornment>
              ) : null,
          },
        }}
      />
      {isFocused && (
        <Stack
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt="10px"
          bgcolor="white"
          border={`1px solid ${theme.palette.darkGrey.main}`}
          borderRadius="10px"
          boxShadow={theme.shadows[10]}
          zIndex={10000}
          justifyContent="start"
          alignItems="center"
          height={`${
            items.length
              ? `min(${
                  (items.length + 1) * (HEIGHT + SPACING) - SPACING
                }px, 280px)`
              : `${HEIGHT}px`
          }`}
          p={1}
          sx={{
            cursor: 'pointer',
          }}
          flex="1 1 auto">
          <Stack
            width="100%"
            height="100%"
            flexGrow={1}
            boxSizing="border-box"
            position="relative">
            <Virtuoso
              endReached={() => {
                if (isFetching || !hasNextPage) return;
                fetchNextPage();
              }}
              data={[null, ...items]}
              itemContent={itemContent}
              style={{
                width: '100%',
                height: '100%',
                minHeight: '100%',
                flexGrow: 1,
              }}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

export default CheckoutItemsSearch;
